import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Home,
  ShoppingCart,
  ClipboardList,
  ClipboardCheck,
  FileSearch,
  Sparkles,
  Printer,
  Clock,
  Inbox,
  Tag,
  Newspaper,
  LayoutGrid,
  Star,
  LifeBuoy,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import StoreHomePage from "@/pages/StoreHomePage";
import StoreDashboard from "@/pages/StoreDashboard";
import OtherOrdersPage from "@/pages/OtherOrdersPage";
import OrderChangePage from "@/pages/OrderChangePage";
import BatchChangeRequestsView from "@/components/BatchChangeRequestsView";
import StorePrintPage from "@/pages/StorePrintPage";
import ArticlesPage from "@/pages/ArticlesPage";
import SeasonalRecommendationsPage from "@/pages/SeasonalRecommendationsPage";
import SeasonalOrdersPage from "@/pages/SeasonalOrdersPage";
import SupportPage from "@/pages/SupportPage";
import { SEASONAL_RECOMMENDATIONS_ENABLED } from "@/config/features";
import { sortCategories } from "@/lib/categoryOrder";
import { categoryIcon } from "@/lib/categoryIcons";
import { isHiddenFromStore } from "@/lib/seasonal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function StoreDashboardWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentView, setCurrentView] = useState("home");
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [availableCatIds, setAvailableCatIds] = useState(null);
  const [availableLabels, setAvailableLabels] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [catRes, labRes, prodRes] = await Promise.all([
          axios.get(`${API}/categories`, getAuthHeaders()).catch(() => ({ data: [] })),
          axios.get(`${API}/product-labels`, getAuthHeaders()).catch(() => ({ data: [] })),
          axios.get(`${API}/products`, getAuthHeaders()).catch(() => ({ data: [] })),
        ]);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
        setLabels(Array.isArray(labRes.data) ? labRes.data : []);
        // その顧客に表示される（＝発注可能な）商品が属するカテゴリ／表示ラベルのみを算出
        const prods = Array.isArray(prodRes.data) ? prodRes.data : [];
        const visible = prods.filter((p) => !isHiddenFromStore(p));
        const ids = new Set(visible.map((p) => p.category_id).filter(Boolean));
        setAvailableCatIds(ids);
        const labelNames = new Set();
        visible.forEach((p) => (p.display_labels || []).forEach((n) => labelNames.add(n)));
        setAvailableLabels(labelNames);
      } catch (e) {
        /* サイドバーのフィルタは取得失敗時も基本ナビは表示 */
      }
    })();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const f = new URLSearchParams(location.search).get("f");
    if (path === "/store" || path === "/store/") {
      setCurrentView("home");
    } else if (path.includes("/other")) {
      setCurrentView("other-order");
    } else if (path.includes("/requests")) {
      setCurrentView("requests");
    } else if (path.includes("/change")) {
      setCurrentView("change-order");
    } else if (path.includes("/print")) {
      setCurrentView("print");
    } else if (path.includes("/articles")) {
      setCurrentView("articles");
    } else if (path.includes("/seasonal-orders")) {
      setCurrentView("seasonal-orders");
    } else if (path.includes("/seasonal-recommendations")) {
      setCurrentView("seasonal-recommendations");
    } else if (path.includes("/support")) {
      setCurrentView("support");
    } else if (path.includes("/order")) {
      setCurrentView(f ? `f:${f}` : "order");
    } else {
      setCurrentView("home");
    }
  }, [location]);

  // フィルタ（商品を探す）
  const filterMenu = [
    { type: "category", label: "商品を探す", collapsible: true },
    { id: "f:all", label: "すべて", icon: LayoutGrid, filterKey: "all" },
    { id: "f:favorites", label: "お気に入り", icon: Star, filterKey: "favorites" },
    { id: "f:history", label: "履歴から選ぶ", icon: Clock, filterKey: "history" },
    { id: "f:fax", label: "FAX履歴", icon: Inbox, filterKey: "fax" },
    { id: "f:seasonal", label: "季節商品", icon: Sparkles, filterKey: "seasonal" },
    // その顧客の商品に付いている表示ラベルのみ表示（該当商品が無いラベルは非表示）。
    // 商品一覧の取得前(null)は従来どおり全ラベルを表示。
    ...labels
      .filter((l) => availableLabels === null || availableLabels.has(l.name))
      .map((l) => ({
        id: `f:label:${l.name}`,
        label: l.name,
        icon: Tag,
        filterKey: `label:${l.name}`,
      })),
  ];

  // その顧客の商品が属するカテゴリのみ表示（登録のないカテゴリは非表示）。
  // 商品一覧の取得前(null)は従来どおり全カテゴリを表示。
  const visibleCategories = sortCategories(categories).filter(
    (c) => availableCatIds === null || availableCatIds.has(c.id)
  );
  const catMenu = visibleCategories.length
    ? [
        { type: "category", label: "カテゴリー", collapsible: true },
        ...visibleCategories.map((c) => {
          const { Icon, color } = categoryIcon(c.name);
          return {
            id: `f:${c.id}`,
            label: c.name,
            icon: Icon,
            iconColor: color,
            filterKey: c.id,
          };
        }),
      ]
    : [];

  const menuItems = [
    { id: "home", label: "ホーム", path: "/store", icon: Home },
    { id: "order", label: "発注入力", path: "/store/order", icon: ShoppingCart },
    ...filterMenu,
    ...catMenu,
    { type: "category", label: "発注メニュー" },
    { id: "other-order", label: "リストなし発注", path: "/store/other", icon: ClipboardList },
    { id: "change-order", label: "発注確認＆変更", path: "/store/change", icon: ClipboardCheck },
    { id: "requests", label: "変更申請状況", path: "/store/requests", icon: FileSearch },
    { id: "print", label: "発注控え印刷", path: "/store/print", icon: Printer },
    { id: "articles", label: "特集・お役立ち情報", path: "/store/articles", icon: Newspaper },
    ...(SEASONAL_RECOMMENDATIONS_ENABLED
      ? [{ id: "seasonal-recommendations", label: "季節のおすすめ", path: "/store/seasonal-recommendations", icon: Sparkles }]
      : []),
    { type: "category", label: "サポート" },
    { id: "support", label: "サポート", path: "/store/support", icon: LifeBuoy },
  ];

  const handleMenuClick = (item) => {
    if (item.filterKey !== undefined) {
      navigate(`/store/order?f=${encodeURIComponent(item.filterKey)}`);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <DashboardLayout
      title="施設様発注用ページ"
      menuItems={menuItems}
      currentView={currentView}
      onMenuClick={handleMenuClick}
      hideMessageBanner={currentView === "home"}
    >
      <Routes>
        <Route path="/" element={<StoreHomePage />} />
        <Route path="/order" element={<StoreDashboard />} />
        <Route path="/other" element={<OtherOrdersPage />} />
        <Route path="/change" element={<OrderChangePage />} />
        {/* 発注書を印刷: サイドバー非表示だがルートは維持（他ページからのリンク互換） */}
        <Route path="/print" element={<StorePrintPage />} />
        <Route path="/articles" element={<ArticlesPage />} />
        <Route path="/seasonal-orders" element={<SeasonalOrdersPage />} />
        <Route path="/seasonal-recommendations" element={<SeasonalRecommendationsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route
          path="/requests"
          element={
            <BatchChangeRequestsView
              title="変更申請状況"
              description="送信した変更申請の進捗（申請中・承認済・却下）と過去履歴を確認できます。"
              scope="store"
            />
          }
        />
      </Routes>
    </DashboardLayout>
  );
}
